import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { 
  ArrowLeft, Bot, Zap, Clock, Brain, Shield, AlertTriangle, 
  TrendingUp, BarChart3, CheckCircle2, Calculator, Users, 
  DollarSign, Percent, ArrowRight, Cpu, X, Settings, ChevronDown, Play
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIBot = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [investAmount, setInvestAmount] = useState(1000);
  const [period, setPeriod] = useState(30);
  const [showBotSettings, setShowBotSettings] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [settingsStep, setSettingsStep] = useState(1); // 1 = settings, 2 = amount input
  const [botAmount, setBotAmount] = useState(1000);
  const [isStarting, setIsStarting] = useState(false);
  
  // Bot settings state
  const [botSettings, setBotSettings] = useState({
    depositRange: '$1000-2000',
    maxDrawdown: '10',
    riskLevel: 'moderate',
    riskPerTrade: '1',
    leverage: 'x3-x5',
    tradingStyle: 'intraday',
    duration: '30' // 7, 30, 60, 180 days
  });

  // ROI based on duration — synced with the profitability calculator
  // (~30%/month compounded over the term): (1.30)^(days/30) - 1
  const getRoiByDuration = (days) => {
    const roiMap = {
      '7': 6.3,
      '30': 30,
      '60': 69,
      '180': 382.6
    };
    return roiMap[days] || 30;
  };
  
  // Расчёт прибыли для калькулятора
  const monthlyRoi = 0.30;
  const dailyRoi = monthlyRoi / 30;
  const expectedProfit = investAmount * dailyRoi * period;
  const totalReturn = investAmount + expectedProfit;

  // Расчёт для бота
  const botRoi = getRoiByDuration(botSettings.duration);
  const botExpectedProfit = botAmount * (botRoi / 100);
  const botTotalReturn = botAmount + botExpectedProfit;

  const handleSettingChange = (key, value) => {
    setBotSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleProceedToAmount = () => {
    setSettingsStep(2);
  };

  const handleStartBot = async () => {
    if (botAmount < 1000) {
      alert('Минимальная сумма для запуска бота — $1000');
      return;
    }

    setIsStarting(true);
    try {
      // Create bot trade
      const response = await axios.post(`${API}/trades/bot`, {
        amount: botAmount,
        duration_days: parseInt(botSettings.duration),
        expected_roi: botRoi,
        settings: botSettings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setShowBotSettings(false);
        setSettingsStep(1);
        alert('🤖 Бот Аврора успешно запущен! Перейдите в Дашборд для отслеживания.');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error starting bot:', error);
      alert(error.response?.data?.detail || 'Ошибка при запуске бота. Проверьте баланс.');
    } finally {
      setIsStarting(false);
    }
  };

  const closeModal = () => {
    setShowBotSettings(false);
    setSettingsStep(1);
  };

  return (
    <div className="min-h-screen bg-[#000000]">
      {/* Header */}
      <header className="border-b border-[#1F1F1F] bg-[#000000]/90 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors">
              <ArrowLeft size={20} />
              <span>Назад</span>
            </Link>
            <div className="flex items-center gap-2">
              <Bot className="text-[#EDEDED]" size={20} />
              <span className="text-[#EDEDED] text-sm font-bold px-2 py-0.5 bg-white/10 rounded">TRADING BOT AI</span>
            </div>
          </div>
        </div>
      </header>

      {/* Bot Settings Modal */}
      {showBotSettings && (
        <div className="fixed inset-0 bg-black/80-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0A0A0C] rounded-2xl border border-[#1F1F1F] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#0A0A0C] border-b border-[#1F1F1F] p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  {settingsStep === 1 ? <Settings className="text-[#EDEDED]" size={20} /> : <Play className="text-[#EDEDED]" size={20} />}
                </div>
                <div>
                  <h2 className="font-chivo font-bold text-xl text-white">
                    {settingsStep === 1 ? 'Настройки бота Аврора' : 'Запуск бота Аврора'}
                  </h2>
                  <p className="text-[#A1A1AA] text-sm">
                    {settingsStep === 1 ? 'Настройте параметры под ваши цели' : 'Введите сумму для инвестирования'}
                  </p>
                </div>
              </div>
              <button 
                onClick={closeModal}
                className="text-[#A1A1AA] hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Step 1: Settings */}
            {settingsStep === 1 && (
              <>
                {/* Modal Content */}
                <div className="p-6 space-y-6">
                  {/* Deposit Range */}
              <div>
                <label className="flex items-center gap-2 text-white font-medium mb-3">
                  <DollarSign size={18} className="text-[#EDEDED]" />
                  Размер депозита
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: '$1000-2000', label: '$1000–2000', recommended: true },
                    { value: '$2000-5000', label: '$2000–5000' },
                    { value: '$5000+', label: '$5000+' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleSettingChange('depositRange', option.value)}
                      className={`relative p-3 rounded-lg border text-sm transition-all ${
                        botSettings.depositRange === option.value
                          ? 'border-white bg-white/10 text-white'
                          : 'border-[#1F1F1F] text-[#A1A1AA] hover:border-[#2E2E2E]/50'
                      }`}
                    >
                      {option.label}
                      {option.recommended && (
                        <span className="absolute -top-2 -right-2 bg-green-500 text-[10px] px-1.5 py-0.5 rounded text-black font-bold">
                          МИН.
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-[#A1A1AA] text-xs mt-2">
                  💡 Минимальный депозит для запуска бота — <span className="text-[#EDEDED]">$1000</span>
                </p>
              </div>

              {/* Max Drawdown */}
              <div>
                <label className="flex items-center gap-2 text-white font-medium mb-3">
                  <TrendingUp size={18} className="text-[#EDEDED]" />
                  Максимальная просадка
                </label>
                <p className="text-[#A1A1AA] text-xs mb-3">Сколько вы готовы потерять, прежде чем бот остановится</p>
                <div className="grid grid-cols-4 gap-2">
                  {['5', '10', '20', '30'].map(val => (
                    <button
                      key={val}
                      onClick={() => handleSettingChange('maxDrawdown', val)}
                      className={`p-3 rounded-lg border text-sm transition-all ${
                        botSettings.maxDrawdown === val
                          ? 'border-white bg-white/10 text-white'
                          : 'border-[#1F1F1F] text-[#A1A1AA] hover:border-[#2E2E2E]/50'
                      }`}
                    >
                      {val}%
                    </button>
                  ))}
                </div>
                <p className="text-[#A1A1AA] text-xs mt-2">
                  ⚠️ При достижении просадки бот закроет все позиции и отправит уведомление
                </p>
              </div>

              {/* Risk Level */}
              <div>
                <label className="flex items-center gap-2 text-white font-medium mb-3">
                  <Shield size={18} className="text-[#EDEDED]" />
                  Уровень риска
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'conservative', label: 'Консервативный', color: 'green', desc: 'Низкий риск, стабильный доход' },
                    { value: 'moderate', label: 'Умеренный', color: 'yellow', desc: 'Баланс риска и доходности' },
                    { value: 'aggressive', label: 'Агрессивный', color: 'red', desc: 'Высокий риск, высокий потенциал' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleSettingChange('riskLevel', option.value)}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        botSettings.riskLevel === option.value
                          ? 'border-white bg-white/10'
                          : 'border-[#1F1F1F] hover:border-[#2E2E2E]/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-3 h-3 rounded-full ${
                          option.color === 'green' ? 'bg-green-500' :
                          option.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></span>
                        <span className="text-white text-sm font-medium">{option.label}</span>
                      </div>
                      <p className="text-[#A1A1AA] text-xs">{option.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className="flex items-center gap-2 text-white font-medium mb-3">
                  <Clock size={18} className="text-[#EDEDED]" />
                  Время работы бота
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: '7', label: '7 дней', roi: '+6.3%' },
                    { value: '30', label: '30 дней', roi: '+30%' },
                    { value: '60', label: '60 дней', roi: '+69%' },
                    { value: '180', label: '180 дней', roi: '+383%' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleSettingChange('duration', option.value)}
                      className={`p-3 rounded-lg border text-sm transition-all ${
                        botSettings.duration === option.value
                          ? 'border-white bg-white/10 text-white'
                          : 'border-[#1F1F1F] text-[#A1A1AA] hover:border-[#2E2E2E]/50'
                      }`}
                    >
                      <div>{option.label}</div>
                      <div className="text-green-500 text-xs font-bold">{option.roi}</div>
                    </button>
                  ))}
                </div>
                <p className="text-[#A1A1AA] text-xs mt-2">
                  📈 Ожидаемый ROI за выбранный период
                </p>
              </div>

              {/* Advanced Settings Toggle */}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-[#EDEDED] hover:text-[#EDEDED] transition-colors"
              >
                <ChevronDown className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`} size={18} />
                <span className="text-sm font-medium">Расширенные настройки</span>
              </button>

              {/* Advanced Settings */}
              {showAdvanced && (
                <div className="space-y-6 pt-4 border-t border-[#1F1F1F]">
                  {/* Risk per Trade */}
                  <div>
                    <label className="flex items-center gap-2 text-white font-medium mb-3">
                      <Percent size={18} className="text-[#EDEDED]" />
                      Риск на сделку (% от депозита)
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {['0.5', '1', '2', '5'].map(val => (
                        <button
                          key={val}
                          onClick={() => handleSettingChange('riskPerTrade', val)}
                          className={`p-3 rounded-lg border text-sm transition-all ${
                            botSettings.riskPerTrade === val
                              ? 'border-white bg-white/10 text-white'
                              : 'border-[#1F1F1F] text-[#A1A1AA] hover:border-[#2E2E2E]/50'
                          }`}
                        >
                          {val}%
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Leverage */}
                  <div>
                    <label className="flex items-center gap-2 text-white font-medium mb-3">
                      <BarChart3 size={18} className="text-[#EDEDED]" />
                      Плечо (leverage)
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'x1-x3', label: 'x1–x3', desc: 'Консервативно' },
                        { value: 'x3-x5', label: 'x3–x5', desc: 'Умеренно' },
                        { value: 'x5-x10', label: 'x5–x10', desc: 'Агрессивно' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => handleSettingChange('leverage', option.value)}
                          className={`p-3 rounded-lg border text-sm transition-all ${
                            botSettings.leverage === option.value
                              ? 'border-white bg-white/10 text-white'
                              : 'border-[#1F1F1F] text-[#A1A1AA] hover:border-[#2E2E2E]/50'
                          }`}
                        >
                          <div>{option.label}</div>
                          <div className="text-[10px] text-[#A1A1AA]">{option.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Trading Style */}
                  <div>
                    <label className="flex items-center gap-2 text-white font-medium mb-3">
                      <Clock size={18} className="text-[#EDEDED]" />
                      Стиль торговли
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'scalping', label: 'Скальпинг', desc: 'Часто, мелкие сделки' },
                        { value: 'intraday', label: 'Интрадей', desc: 'Внутри дня' },
                        { value: 'swing', label: 'Свинг', desc: 'Реже, дольше держим' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => handleSettingChange('tradingStyle', option.value)}
                          className={`p-3 rounded-lg border text-sm transition-all ${
                            botSettings.tradingStyle === option.value
                              ? 'border-white bg-white/10 text-white'
                              : 'border-[#1F1F1F] text-[#A1A1AA] hover:border-[#2E2E2E]/50'
                          }`}
                        >
                          <div>{option.label}</div>
                          <div className="text-[10px] text-[#A1A1AA]">{option.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

                {/* Modal Footer - Step 1 */}
                <div className="sticky bottom-0 bg-[#0A0A0C] border-t border-[#1F1F1F] p-6">
                  <button
                    onClick={handleProceedToAmount}
                    className="w-full btn-primary flex items-center justify-center gap-2 text-lg py-4"
                  >
                    Далее
                    <ArrowRight size={20} />
                  </button>
                  <p className="text-[#A1A1AA] text-xs text-center mt-3">
                    Ожидаемый ROI за {botSettings.duration} дней: <span className="text-green-500 font-bold">+{getRoiByDuration(botSettings.duration)}%</span>
                  </p>
                </div>
              </>
            )}

            {/* Step 2: Amount Input */}
            {settingsStep === 2 && (
              <>
                <div className="p-6 space-y-6">
                  {/* Summary of settings */}
                  <div className="bg-[#000000] rounded-xl p-4 border border-[#1F1F1F]">
                    <h3 className="text-white font-medium mb-3">Выбранные настройки:</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-[#A1A1AA]">Время работы:</div>
                      <div className="text-white font-medium">{botSettings.duration} дней</div>
                      <div className="text-[#A1A1AA]">Ожидаемый ROI:</div>
                      <div className="text-green-500 font-bold">+{botRoi}%</div>
                      <div className="text-[#A1A1AA]">Уровень риска:</div>
                      <div className="text-white">{botSettings.riskLevel === 'conservative' ? 'Консервативный' : botSettings.riskLevel === 'moderate' ? 'Умеренный' : 'Агрессивный'}</div>
                    </div>
                  </div>

                  {/* Amount Input */}
                  <div>
                    <label className="flex items-center gap-2 text-white font-medium mb-3">
                      <DollarSign size={18} className="text-[#EDEDED]" />
                      Сумма инвестиции
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={botAmount || ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setBotAmount(val === '' ? 0 : Number(val));
                        }}
                        onFocus={(e) => e.target.select()}
                        className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg pl-8 pr-4 py-4 text-white text-2xl font-mono focus:border-white focus:outline-none"
                        placeholder="Введите сумму"
                      />
                    </div>
                    <div className="flex gap-2 mt-3">
                      {[1000, 2000, 5000, 10000].map(val => (
                        <button
                          key={val}
                          onClick={() => setBotAmount(val)}
                          className={`flex-1 py-2 rounded text-sm ${botAmount === val ? 'bg-[#FF8A1E] text-white font-bold' : 'bg-[#1F1F1F] text-[#A1A1AA] hover:bg-[#3E3E3E]'}`}
                        >
                          ${val.toLocaleString()}
                        </button>
                      ))}
                    </div>
                    {botAmount < 1000 && (
                      <p className="text-red-500 text-sm mt-2">⚠️ Минимальная сумма — $1000</p>
                    )}
                  </div>

                  {/* Profit Calculation */}
                  <div className="bg-gradient-to-r from-white/10 to-green-500/10 rounded-xl p-6 border border-white/30">
                    <h3 className="text-white font-medium mb-4">Расчёт прибыли</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-[#A1A1AA]">Инвестиция:</span>
                        <span className="text-white font-mono">${botAmount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A1A1AA]">ROI за {botSettings.duration} дней:</span>
                        <span className="text-green-500 font-mono">+{botRoi}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A1A1AA]">Ожидаемая прибыль:</span>
                        <span className="text-green-500 font-mono font-bold">+${botExpectedProfit.toLocaleString()}</span>
                      </div>
                      <div className="border-t border-[#1F1F1F] pt-3 flex justify-between">
                        <span className="text-white font-medium">Итого получите:</span>
                        <span className="text-[#EDEDED] font-mono font-bold text-xl">${botTotalReturn.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer - Step 2 */}
                <div className="sticky bottom-0 bg-[#0A0A0C] border-t border-[#1F1F1F] p-6 space-y-3">
                  <button
                    onClick={handleStartBot}
                    disabled={botAmount < 1000 || isStarting}
                    className={`w-full flex items-center justify-center gap-2 text-lg py-4 rounded font-bold transition-all ${
                      botAmount >= 1000 && !isStarting
                        ? 'btn-primary'
                        : 'bg-[#1F1F1F] text-[#A1A1AA] cursor-not-allowed'
                    }`}
                  >
                    {isStarting ? (
                      <>Запуск...</>
                    ) : (
                      <>
                        <Play size={20} />
                        Запустить бота на ${botAmount.toLocaleString()}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setSettingsStep(1)}
                    className="w-full py-3 text-[#A1A1AA] hover:text-white transition-colors"
                  >
                    ← Вернуться к настройкам
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Hero Section - Bybit Style */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="relative z-10">
              <h1 className="font-chivo font-bold text-4xl sm:text-5xl lg:text-6xl text-[#EDEDED] mb-6">
                ИИ Аврора от Bybit
              </h1>
              <p className="text-[#A1A1AA] text-xl mb-8">
                Трейдинг нон-стоп: ваше ИИ-преимущество круглые сутки
              </p>
              <button 
                onClick={() => setShowBotSettings(true)}
                className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
              >
                Начать работу с ботом Аврора
                <ArrowRight size={20} />
              </button>
            </div>
            
            {/* Right - AI Visual */}
            <div className="relative">
              {/* Cyan/Teal background with grid */}
              <div className="relative w-full h-[400px] rounded-2xl overflow-hidden bg-gradient-to-br from-[#00D4AA] to-[#00A3B4]">
                {/* Grid pattern */}
                <div 
                  className="absolute inset-0 opacity-30"
                  style={{
                    backgroundImage: `
                      linear-gradient(rgba(0,0,0,0.3) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(0,0,0,0.3) 1px, transparent 1px)
                    `,
                    backgroundSize: '30px 30px'
                  }}
                ></div>
                
                {/* AI Head silhouette */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    {/* Head shape */}
                    <svg viewBox="0 0 200 280" className="w-48 h-64 sm:w-64 sm:h-80">
                      <defs>
                        <linearGradient id="headGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#1a1a2e" />
                          <stop offset="100%" stopColor="#0f0f1a" />
                        </linearGradient>
                      </defs>
                      {/* Head silhouette */}
                      <path 
                        d="M100 20 C40 20 20 80 20 140 C20 200 50 260 100 260 C150 260 180 200 180 140 C180 80 160 20 100 20" 
                        fill="url(#headGrad)"
                      />
                      {/* CPU/Chip in center */}
                      <rect x="70" y="100" width="60" height="60" fill="#000000" stroke="#EDEDED" strokeWidth="2" rx="4"/>
                      <rect x="85" y="115" width="30" height="30" fill="#1a1a2e" stroke="#EDEDED" strokeWidth="1"/>
                      {/* Connection lines */}
                      <line x1="70" y1="130" x2="50" y2="130" stroke="#EDEDED" strokeWidth="2"/>
                      <line x1="130" y1="130" x2="150" y2="130" stroke="#EDEDED" strokeWidth="2"/>
                      <line x1="100" y1="100" x2="100" y2="70" stroke="#EDEDED" strokeWidth="2"/>
                      <line x1="100" y1="160" x2="100" y2="190" stroke="#EDEDED" strokeWidth="2"/>
                      {/* Nodes */}
                      <circle cx="50" cy="80" r="8" fill="#000000" stroke="#EDEDED" strokeWidth="2"/>
                      <circle cx="150" cy="80" r="8" fill="#000000" stroke="#EDEDED" strokeWidth="2"/>
                      <circle cx="50" cy="180" r="8" fill="#000000" stroke="#EDEDED" strokeWidth="2"/>
                      <circle cx="150" cy="180" r="8" fill="#000000" stroke="#EDEDED" strokeWidth="2"/>
                      <circle cx="100" cy="50" r="8" fill="#000000" stroke="#EDEDED" strokeWidth="2"/>
                      <circle cx="70" cy="200" r="6" fill="#000000" stroke="#EDEDED" strokeWidth="2"/>
                      <circle cx="130" cy="200" r="6" fill="#000000" stroke="#EDEDED" strokeWidth="2"/>
                      {/* More connection lines */}
                      <line x1="50" y1="80" x2="70" y2="100" stroke="#EDEDED" strokeWidth="1" opacity="0.6"/>
                      <line x1="150" y1="80" x2="130" y2="100" stroke="#EDEDED" strokeWidth="1" opacity="0.6"/>
                      <line x1="50" y1="180" x2="70" y2="160" stroke="#EDEDED" strokeWidth="1" opacity="0.6"/>
                      <line x1="150" y1="180" x2="130" y2="160" stroke="#EDEDED" strokeWidth="1" opacity="0.6"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About AI Bots - Text Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-t border-[#1F1F1F]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#0A0A0C] rounded-2xl border border-[#1F1F1F] p-8 mb-8">
            <p className="text-white/90 leading-relaxed mb-6">
              Искусственный интеллект интегрируется в нашу жизнь стремительными темпами и в самых 
              разнообразных сферах, и торговля цифровыми активами не является исключением. 
              Использование алгоритмов ИИ в трейдинговых операциях позволяет значительно повысить 
              их эффективность и скорость, а также минимизировать риски и увеличить потенциальную прибыль.
            </p>
            <p className="text-[#A1A1AA] leading-relaxed">
              В этом разделе мы разберём преимущества и особенности подобных инструментов, а также 
              рассмотрим возможности использования ботов с ИИ для торговли криптовалютами.
            </p>
          </div>

          {/* What is AI Bot */}
          <div className="bg-[#0A0A0C] rounded-2xl border border-[#1F1F1F] p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="text-[#EDEDED]" size={24} />
              </div>
              <h2 className="font-chivo font-bold text-2xl text-white">Торговые боты с ИИ: что это и как работает?</h2>
            </div>
            
            <p className="text-white/90 leading-relaxed mb-6">
              Торговые боты с искусственным интеллектом — это программное обеспечение, которое использует 
              ИИ-алгоритмы для анализа рыночных данных, заключения сделок, управления инвестиционным 
              портфелем и осуществления других операций с цифровыми активами.
            </p>
            
            <p className="text-[#A1A1AA] leading-relaxed mb-8">
              Как правило, такие боты работают автономно, что позволяет трейдерам заключать сделки 24/7, 
              максимально используя рыночные возможности. Кроме того, торгового бота можно настроить 
              в соответствии с финансовыми целями, допустимым уровнем риска и стратегией конкретного трейдера.
            </p>

            <h3 className="font-chivo font-bold text-lg text-white mb-4">Базовый алгоритм работы ИИ-ботов включает три основных этапа:</h3>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-4 bg-[#000000] rounded-lg p-4">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-[#000000]">1</span>
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">Мониторинг и сбор данных</h4>
                  <p className="text-[#A1A1AA] text-sm">
                    Сбор информации из различных источников: криптобирж, лент новостей, соцсетей и т.д.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-[#000000] rounded-lg p-4">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-[#000000]">2</span>
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">Анализ информации</h4>
                  <p className="text-[#A1A1AA] text-sm">
                    Анализ динамики цен, объёмов торгов, настроений сообщества и других показателей 
                    для выявления потенциальных торговых возможностей.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-[#000000] rounded-lg p-4">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-[#000000]">3</span>
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">Размещение ордеров</h4>
                  <p className="text-[#A1A1AA] text-sm">
                    Покупка или продажа цифровых активов на основе проведённого анализа и 
                    с учётом определённых пользователем параметров.
                  </p>
                </div>
              </div>
            </div>
            
            <p className="text-[#A1A1AA] leading-relaxed p-4 bg-[#000000] rounded-lg border-l-4 border-white">
              Таким образом, торговый бот в сочетании с искусственным интеллектом может обеспечить 
              полностью автономное создание и исполнение сделок, требуя от трейдера только проверки 
              результатов и соответствующей коррекции торговой стратегии.
            </p>
          </div>

          {/* Advantages */}
          <div className="bg-[#0A0A0C] rounded-2xl border border-[#1F1F1F] p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="text-green-500" size={24} />
              </div>
              <h2 className="font-chivo font-bold text-2xl text-white">Преимущества торговых ИИ-ботов</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-[#000000] rounded-lg p-5 border border-[#1F1F1F]">
                <div className="flex items-center gap-3 mb-3">
                  <Brain className="text-green-500" size={20} />
                  <h4 className="font-bold text-white">Безэмоциональность</h4>
                </div>
                <p className="text-[#A1A1AA] text-sm">
                  Боты не поддаются эмоциям вроде страха или жадности, которые могут влиять на 
                  человеческие решения и приводить к снижению торговых результатов.
                </p>
              </div>
              
              <div className="bg-[#000000] rounded-lg p-5 border border-[#1F1F1F]">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="text-green-500" size={20} />
                  <h4 className="font-bold text-white">Работа 24/7</h4>
                </div>
                <p className="text-[#A1A1AA] text-sm">
                  Боты могут работать круглосуточно, используя возможности для получения доходов 
                  независимо от того, когда они появляются.
                </p>
              </div>
              
              <div className="bg-[#000000] rounded-lg p-5 border border-[#1F1F1F]">
                <div className="flex items-center gap-3 mb-3">
                  <Zap className="text-green-500" size={20} />
                  <h4 className="font-bold text-white">Эффективность</h4>
                </div>
                <p className="text-[#A1A1AA] text-sm">
                  Алгоритм может реагировать на изменения рынка и совершать торговые операции 
                  быстрее и точнее человека, используя торговые возможности даже если они незамечены трейдером.
                </p>
              </div>
              
              <div className="bg-[#000000] rounded-lg p-5 border border-[#1F1F1F]">
                <div className="flex items-center gap-3 mb-3">
                  <BarChart3 className="text-green-500" size={20} />
                  <h4 className="font-bold text-white">Диверсификация</h4>
                </div>
                <p className="text-[#A1A1AA] text-sm">
                  Боты могут торговать на нескольких криптовалютных биржах одновременно. 
                  Трейдер может запустить несколько алгоритмов с различными стратегиями для диверсификации инвестиций.
                </p>
              </div>
            </div>
          </div>

          {/* Risks and Protection */}
          <div className="bg-[#0A0A0C] rounded-2xl border border-[#1F1F1F] p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Shield className="text-[#EDEDED]" size={24} />
              </div>
              <h2 className="font-chivo font-bold text-2xl text-white">Возможные риски и защита</h2>
            </div>
            
            <p className="text-[#A1A1AA] mb-6">
              Стоит учитывать, что торговые боты имеют ряд особенностей, связанных с технологией. 
              Однако благодаря современным решениям большинство рисков минимизированы:
            </p>
            
            <div className="space-y-4">
              <div className="bg-[#000000] rounded-lg p-5 border border-[#1F1F1F] border-l-4 border-l-white">
                <h4 className="font-bold text-white mb-2">Технические особенности</h4>
                <p className="text-[#A1A1AA] text-sm mb-2">
                  Любое ПО может содержать ошибки.
                </p>
                <p className="text-green-400 text-sm flex items-center gap-2">
                  <CheckCircle2 size={16} />
                  <span><strong>Решение:</strong> Многоуровневая проверка и тестирование всех операций перед исполнением.</span>
                </p>
              </div>
              
              <div className="bg-[#000000] rounded-lg p-5 border border-[#1F1F1F] border-l-4 border-l-white">
                <h4 className="font-bold text-white mb-2">Адаптация к рынку</h4>
                <p className="text-[#A1A1AA] text-sm mb-2">
                  ИИ может не успевать за резкими изменениями.
                </p>
                <p className="text-green-400 text-sm flex items-center gap-2">
                  <CheckCircle2 size={16} />
                  <span><strong>Решение:</strong> Настраиваемые стоп-лоссы и лимиты убытков защищают капитал в любых условиях.</span>
                </p>
              </div>
              
              <div className="bg-[#000000] rounded-lg p-5 border border-[#1F1F1F] border-l-4 border-l-white">
                <h4 className="font-bold text-white mb-2">Безопасность</h4>
                <p className="text-[#A1A1AA] text-sm mb-2">
                  Вопрос защиты данных актуален для любых онлайн-сервисов.
                </p>
                <p className="text-green-400 text-sm flex items-center gap-2">
                  <CheckCircle2 size={16} />
                  <span><strong>Решение:</strong> Банковский уровень шифрования, 2FA-аутентификация и страховой фонд.</span>
                </p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
              <p className="text-green-400 text-sm">
                <strong>Важно:</strong> Перед использованием ИИ-ботов важно провести собственное исследование. 
                Тщательный анализ поможет принять правильное решение об использовании конкретного сервиса.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-t border-[#1F1F1F]">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#0A0A0C] rounded-2xl border border-[#1F1F1F] p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Calculator className="text-[#EDEDED]" size={24} />
              </div>
              <h2 className="font-chivo font-bold text-2xl text-white">Калькулятор доходности</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Inputs */}
              <div className="space-y-6">
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Сумма инвестиции ($)</label>
                  <input 
                    type="text"
                    inputMode="numeric"
                    value={investAmount || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setInvestAmount(val === '' ? 0 : Number(val));
                    }}
                    onFocus={(e) => e.target.select()}
                    className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:border-white focus:outline-none"
                    placeholder="Введите сумму"
                  />
                  <div className="flex gap-2 mt-2">
                    {[500, 1000, 5000, 10000].map(val => (
                      <button 
                        key={val}
                        onClick={() => setInvestAmount(val)}
                        className={`px-3 py-1 rounded text-xs ${investAmount === val ? 'bg-[#FF8A1E] text-white' : 'bg-[#1F1F1F] text-[#A1A1AA]'}`}
                      >
                        ${val}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Период (дней)</label>
                  <input 
                    type="text"
                    inputMode="numeric"
                    value={period || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setPeriod(val === '' ? 0 : Math.min(365, Number(val)));
                    }}
                    onFocus={(e) => e.target.select()}
                    className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:border-white focus:outline-none"
                    placeholder="Введите период"
                  />
                  <div className="flex gap-2 mt-2">
                    {[7, 30, 90, 180].map(val => (
                      <button 
                        key={val}
                        onClick={() => setPeriod(val)}
                        className={`px-3 py-1 rounded text-xs ${period === val ? 'bg-[#FF8A1E] text-white' : 'bg-[#1F1F1F] text-[#A1A1AA]'}`}
                      >
                        {val} дн.
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Results */}
              <div className="bg-[#000000] rounded-xl p-6 border border-[#1F1F1F]">
                <h3 className="text-[#A1A1AA] text-sm mb-4">Прогнозируемый результат*</h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-[#A1A1AA]">Инвестиция:</span>
                    <span className="text-white font-mono">${investAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#A1A1AA]">Ожидаемая прибыль:</span>
                    <span className="text-green-500 font-mono font-bold">+${expectedProfit.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-[#1F1F1F] pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-white font-medium">Итого:</span>
                      <span className="text-[#EDEDED] font-mono font-bold text-2xl">${totalReturn.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[#A1A1AA]">ROI за период:</span>
                    <span className="text-green-500 font-medium">+{((expectedProfit / investAmount) * 100).toFixed(1)}%</span>
                  </div>
                </div>
                
                <p className="text-[#A1A1AA] text-xs mt-4">
                  * Расчёт основан на средней доходности 25%/мес. Реальные результаты могут отличаться.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white/10 border border-white/30 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-[#EDEDED] flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-chivo font-bold text-[#EDEDED] mb-2">Дисклеймер</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">
                  Информация представлена исключительно в ознакомительных целях и не является 
                  финансовой рекомендацией или призывом к действию. Торговля криптовалютами 
                  сопряжена с рисками. Всегда проводите собственные исследования.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-t border-[#1F1F1F]">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-chivo font-bold text-2xl text-white text-center mb-8">Как работает бот Аврора</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#0A0A0C] rounded-xl border border-[#1F1F1F] p-6">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                <span className="font-bold text-black text-xl">1</span>
              </div>
              <h3 className="font-bold text-white mb-2">Мониторинг рынка</h3>
              <p className="text-[#A1A1AA] text-sm">
                Бот анализирует данные с криптобирж, новости и социальные сети 24/7
              </p>
            </div>
            
            <div className="bg-[#0A0A0C] rounded-xl border border-[#1F1F1F] p-6">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                <span className="font-bold text-black text-xl">2</span>
              </div>
              <h3 className="font-bold text-white mb-2">ИИ-анализ</h3>
              <p className="text-[#A1A1AA] text-sm">
                Алгоритмы машинного обучения выявляют прибыльные торговые возможности
              </p>
            </div>
            
            <div className="bg-[#0A0A0C] rounded-xl border border-[#1F1F1F] p-6">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                <span className="font-bold text-black text-xl">3</span>
              </div>
              <h3 className="font-bold text-white mb-2">Автоматические сделки</h3>
              <p className="text-[#A1A1AA] text-sm">
                Бот размещает ордера автоматически с учётом ваших параметров риска
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-t border-[#1F1F1F]">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-chivo font-bold text-2xl text-white text-center mb-8">Преимущества бота Аврора</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-[#0A0A0C] rounded-lg p-5 border border-[#1F1F1F] flex items-start gap-4">
              <Brain className="text-green-500 flex-shrink-0" size={24} />
              <div>
                <h4 className="font-bold text-white mb-1">Безэмоциональность</h4>
                <p className="text-[#A1A1AA] text-sm">Бот не поддаётся страху или жадности — только холодный расчёт</p>
              </div>
            </div>
            
            <div className="bg-[#0A0A0C] rounded-lg p-5 border border-[#1F1F1F] flex items-start gap-4">
              <Clock className="text-green-500 flex-shrink-0" size={24} />
              <div>
                <h4 className="font-bold text-white mb-1">Работа 24/7</h4>
                <p className="text-[#A1A1AA] text-sm">Круглосуточная торговля без перерывов и выходных</p>
              </div>
            </div>
            
            <div className="bg-[#0A0A0C] rounded-lg p-5 border border-[#1F1F1F] flex items-start gap-4">
              <Zap className="text-green-500 flex-shrink-0" size={24} />
              <div>
                <h4 className="font-bold text-white mb-1">Скорость реакции</h4>
                <p className="text-[#A1A1AA] text-sm">Мгновенное исполнение сделок быстрее любого трейдера</p>
              </div>
            </div>
            
            <div className="bg-[#0A0A0C] rounded-lg p-5 border border-[#1F1F1F] flex items-start gap-4">
              <Shield className="text-green-500 flex-shrink-0" size={24} />
              <div>
                <h4 className="font-bold text-white mb-1">Защита Bybit</h4>
                <p className="text-[#A1A1AA] text-sm">Банковское шифрование, 2FA и страховой фонд</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-t border-[#1F1F1F]">
        <div className="max-w-3xl mx-auto text-center">
          <Cpu className="text-[#EDEDED] mx-auto mb-4" size={48} />
          <h2 className="font-chivo font-bold text-3xl text-white mb-4">
            Готовы начать с ботом Аврора?
          </h2>
          <p className="text-[#A1A1AA] mb-8 max-w-lg mx-auto">
            Подключите ИИ-бота и начните получать пассивный доход от автоматической торговли криптовалютами
          </p>
          <button 
            onClick={() => setShowBotSettings(true)}
            className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
          >
            Начать работу с ботом Аврора
            <ArrowRight size={20} />
          </button>
          <p className="text-[#A1A1AA] text-sm mt-4">
            Минимальный депозит для стабильного дохода — <span className="text-[#EDEDED]">$1000</span> • Вывод в любой момент
          </p>
        </div>
      </section>
    </div>
  );
};

export default AIBot;
